/* Transporte das chamadas de API do painel.

   O objetivo do desenho: quando o backend do Analytics for portado para o AVA,
   NENHUMA tela muda — só o flag. As telas chamam `api.*`, o api.js declara os
   paths reais, e é aqui que se decide se a resposta vem da rede ou do mock.

        telas  ->  api.js (22 funções, paths e params reais)
                        |
                   transport.js
                     |        \
              mockRouter    axios do AVA (URL.api + Bearer)

   Flag: REACT_APP_IE_MOCK. DESLIGADO por padrão — o mock só entra quando alguém
   pede explicitamente `REACT_APP_IE_MOCK=on`.

   O padrão era o contrário na Etapa 1, e com razão: o backend não existia, então
   a única forma de ver o painel era pelas fixtures. A Etapa 2 entregou as 21
   rotas, e aí o padrão invertido virou uma armadilha — as variáveis do CRA são
   assadas no BUILD, o deploy é FTP e não define nenhuma REACT_APP_*, e nenhum
   .env é versionado. Ou seja: publicado, o painel apareceria no menu servindo
   dado de FIXTURE, com cara de número real. Errado em silêncio, que é o pior
   jeito de errar.

   Para desenvolver contra as fixtures (sem backend de pé), ponha
   REACT_APP_IE_MOCK=on no .env.local, que está fora do git.

   As fixtures são carregadas por import() dinâmico, então os 594 KB de JSON
   ficam num chunk separado e não entram no bundle de quem não abre o painel. */

import URL from "../../../config/api";
import axios from "../../../config/axios";
import { store } from "../../../store/store";

/* Prefixo do serviço quando o backend estiver no AVA. Concentrado aqui: a
   virada é uma linha. Ver pendência P3 do plano. */
const PREFIXO_API = "analytics";

export const MOCK_LIGADO =
  String(process.env.REACT_APP_IE_MOCK || "off").toLowerCase() === "on";

/* Postgres serializa colunas `numeric` (saída de ROUND(), por exemplo) como
   STRING em JSON — só integer/float nativos viram number. Isso faz
   "nota_media": "8.7" chegar como string e quebrar contas silenciosamente
   (concatenação em vez de soma). Normalizamos uma vez, aqui, em vez de
   espalhar toNumber() por cada tela. Decisão herdada do projeto de origem, que
   estava correta. */
const NUM_STRING = /^-?\d+(\.\d+)?$/;

export function normalizeNumbers(value) {
  if (Array.isArray(value)) return value.map(normalizeNumbers);
  if (value !== null && typeof value === "object") {
    const out = {};
    Object.keys(value).forEach(k => {
      out[k] = normalizeNumbers(value[k]);
    });
    return out;
  }
  if (typeof value === "string" && NUM_STRING.test(value)) return Number(value);
  return value;
}

const querystring = params => {
  if (!params) return "";
  const pares = Object.keys(params)
    .filter(
      k => params[k] !== undefined && params[k] !== null && params[k] !== ""
    )
    .map(
      k => `${encodeURIComponent(k)}=${encodeURIComponent(String(params[k]))}`
    );
  return pares.length ? `?${pares.join("&")}` : "";
};

let mockRouterPromise = null;
const carregarMock = () => {
  if (!mockRouterPromise) {
    mockRouterPromise = import("./mock/router").then(m => m.resolverMock);
  }
  return mockRouterPromise;
};

const tokenDoRedux = () => {
  try {
    return store.getState().user.token;
  } catch (err) {
    return null;
  }
};

/* O backend do AVA responde no envelope padrão da casa, { success, data } — as
   ~25 rotas de statistics/* já fazem isso, e o resto do front consome como
   `resp.data.data`. O mock, por outro lado, devolve o payload cru, que é o
   formato do Flask de origem.

   Este é o único ponto que precisa saber disso: as telas continuam recebendo o
   payload. O desembrulho é tolerante de propósito — só desce um nível quando
   reconhece o envelope, então mock e backend real passam pelo mesmo caminho.

   `success: false` merece tratamento explícito porque o middleware de papel do
   AVA (EnsureUserHasRole) nega com HTTP 200 no corpo, e não com 4xx. Sem isto,
   um acesso negado chegaria às telas como dado indefinido em vez de erro. */
const desembrulhar = payload => {
  const ehEnvelope =
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    "success" in payload;

  if (!ehEnvelope) return payload;

  if (payload.success === false) {
    const erro = payload.error || "Não foi possível carregar os dados.";
    throw new Error(
      payload.errors ? Object.values(payload.errors)[0][0] : erro
    );
  }

  return payload.data;
};

export async function get(path, params) {
  if (MOCK_LIGADO) {
    const resolver = await carregarMock();
    return normalizeNumbers(await resolver("GET", path, params));
  }

  const resp = await axios.get(
    `${URL.api}${PREFIXO_API}${path}${querystring(params)}`,
    { headers: { authorization: `Bearer ${tokenDoRedux()}` } }
  );
  return normalizeNumbers(desembrulhar(resp.data));
}

export async function post(path, params) {
  if (MOCK_LIGADO) {
    const resolver = await carregarMock();
    return normalizeNumbers(await resolver("POST", path, params));
  }

  const resp = await axios.post(
    `${URL.api}${PREFIXO_API}${path}${querystring(params)}`,
    null,
    { headers: { authorization: `Bearer ${tokenDoRedux()}` } }
  );
  return normalizeNumbers(desembrulhar(resp.data));
}
